import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { PagedResponse, SearchParams } from './types';

// ── Lead Scoring Rule ──

export interface LeadScoringRule {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  entity_type: string;
  field: string;
  operator: string;
  value: string;
  score: number;
  priority: number;
  is_active: boolean;
  category: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface CreateLeadScoringRuleRequest {
  name: string;
  description?: string;
  entity_type: string;
  field: string;
  operator: string;
  value: string;
  score: number;
  priority: number;
  is_active: boolean;
  category?: string;
}

export interface UpdateLeadScoringRuleRequest {
  name?: string;
  description?: string;
  entity_type?: string;
  field?: string;
  operator?: string;
  value?: string;
  score?: number;
  priority?: number;
  is_active?: boolean;
  category?: string;
}

export interface LeadScoringRuleFilters extends SearchParams {
  entity_type?: string;
  category?: string;
  is_active?: string;
}

// ── Lead Score ──

export interface LeadScore {
  id: string;
  tenant_id: string;
  entity_type: string;
  entity_id: string;
  total_score: number;
  grade: string;
  score_breakdown: string;
  rules_matched: number;
  rules_evaluated: number;
  scored_at: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface ScoreEntityRequest {
  entity_type: string;
  entity_id: string;
}

export interface LeadScoreFilters extends SearchParams {
  entity_type?: string;
  grade?: string;
}

// ── Known Constants ──

export const SCORING_ENTITY_TYPES = ['customer', 'contact', 'opportunity'];

export const SCORING_OPERATORS = [
  'equals', 'not_equals', 'contains',
  'greater_than', 'less_than',
  'in', 'not_in', 'exists', 'not_exists',
];

export const LEAD_GRADES = ['A', 'B', 'C', 'D', 'F'];

export const SCORABLE_FIELDS: Record<string, string[]> = {
  customer: [
    'name', 'email', 'phone', 'company_name', 'industry', 'company_size',
    'source', 'status', 'type', 'website', 'tags', 'rating', 'annual_revenue',
    'address', 'city', 'state', 'country', 'notes',
  ],
  contact: [
    'first_name', 'last_name', 'email', 'phone', 'title', 'department',
    'status', 'source', 'tags', 'notes',
  ],
  opportunity: [
    'name', 'amount', 'currency', 'probability', 'status', 'source',
    'close_date', 'owner_id', 'tags', 'notes',
  ],
};

// ── Lead Scoring Rule API ──

export const getScoringRules = (params: LeadScoringRuleFilters = {}) =>
  apiGet<PagedResponse<LeadScoringRule>>('/api/v1/lead-scoring-rules', params);

export const getScoringRule = (id: string) =>
  apiGet<LeadScoringRule>(`/api/v1/lead-scoring-rules/${id}`);

export const createScoringRule = (data: CreateLeadScoringRuleRequest) =>
  apiPost<LeadScoringRule>('/api/v1/lead-scoring-rules', data);

export const updateScoringRule = (id: string, data: UpdateLeadScoringRuleRequest) =>
  apiPut<LeadScoringRule>(`/api/v1/lead-scoring-rules/${id}`, data);

export const deleteScoringRule = (id: string) =>
  apiDelete(`/api/v1/lead-scoring-rules/${id}`);

// ── Lead Score API ──

export const getLeadScores = (params: LeadScoreFilters = {}) =>
  apiGet<PagedResponse<LeadScore>>('/api/v1/lead-scores', params);

export const getLeadScore = (id: string) =>
  apiGet<LeadScore>(`/api/v1/lead-scores/${id}`);

export const scoreEntity = (data: ScoreEntityRequest) =>
  apiPost<LeadScore>('/api/v1/lead-scores/score', data);

export const scoreAllEntities = (entityType: string) =>
  apiPost<{ entity_type: string; entities_scored: number }>(
    `/api/v1/lead-scores/score-all?entity_type=${encodeURIComponent(entityType)}`, {}
  );

export const deleteLeadScore = (id: string) =>
  apiDelete(`/api/v1/lead-scores/${id}`);

// ── Analytics Snapshot ──

export interface AnalyticsSnapshot {
  id: string;
  tenant_id: string;
  snapshot_type: string;
  entity_type: string;
  snapshot_date: string;
  period_start: string;
  period_end: string;
  metrics: string;
  summary: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface GenerateSnapshotRequest {
  snapshot_type: string;
  entity_type: string;
}

export interface SnapshotFilters extends SearchParams {
  entity_type?: string;
  snapshot_type?: string;
}

// ── Forecast ──

export interface Forecast {
  id: string;
  tenant_id: string;
  forecast_type: string;
  entity_type: string;
  period_start: string;
  period_end: string;
  forecast_value: number;
  actual_value: number | null;
  confidence_level: number;
  method: string;
  parameters: string;
  status: string;
  notes: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface GenerateForecastRequest {
  forecast_type: string;
  entity_type: string;
  method: string;
  periods: number;
}

export interface UpdateForecastActualRequest {
  actual_value: number;
  notes?: string;
}

export interface ForecastFilters extends SearchParams {
  forecast_type?: string;
  status?: string;
  method?: string;
}

export interface ForecastAccuracyResult {
  forecast_type: string;
  total_forecasts: number;
  forecasts_with_actuals: number;
  mean_absolute_percentage_error: number;
  mean_absolute_error: number;
}

// ── Snapshot & Forecast Constants ──

export const SNAPSHOT_TYPES = ['daily', 'weekly', 'monthly', 'quarterly'];

export const SNAPSHOT_ENTITY_TYPES = ['customer', 'opportunity', 'pipeline', 'revenue', 'overall'];

export const FORECAST_TYPES = [
  'revenue', 'customer_growth', 'opportunity_count',
  'pipeline_value', 'active_opportunities',
];

export const FORECAST_METHODS = [
  'linear_regression', 'moving_average', 'weighted_moving_average',
];

export const FORECAST_STATUSES = ['active', 'expired', 'superseded'];

// ── Analytics Snapshot API ──

export const getSnapshots = (params: SnapshotFilters = {}) =>
  apiGet<PagedResponse<AnalyticsSnapshot>>('/api/v1/analytics-snapshots', params);

export const getSnapshot = (id: string) =>
  apiGet<AnalyticsSnapshot>(`/api/v1/analytics-snapshots/${id}`);

export const getSnapshotTrend = (entityType: string, snapshotType: string, dateFrom?: string, dateTo?: string) => {
  const params: Record<string, string> = { entity_type: entityType, snapshot_type: snapshotType };
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  return apiGet<AnalyticsSnapshot[]>('/api/v1/analytics-snapshots/trend', params);
};

export const generateSnapshot = (data: GenerateSnapshotRequest) =>
  apiPost<AnalyticsSnapshot>('/api/v1/analytics-snapshots/generate', data);

export const deleteSnapshot = (id: string) =>
  apiDelete(`/api/v1/analytics-snapshots/${id}`);

// ── Forecast API ──

export const getForecasts = (params: ForecastFilters = {}) =>
  apiGet<PagedResponse<Forecast>>('/api/v1/forecasts', params);

export const getForecast = (id: string) =>
  apiGet<Forecast>(`/api/v1/forecasts/${id}`);

export const generateForecast = (data: GenerateForecastRequest) =>
  apiPost<Forecast[]>('/api/v1/forecasts/generate', data);

export const getForecastAccuracy = (forecastType: string) =>
  apiGet<ForecastAccuracyResult>('/api/v1/forecasts/accuracy', { forecast_type: forecastType });

export const updateForecastActual = (id: string, data: UpdateForecastActualRequest) =>
  apiPut<Forecast>(`/api/v1/forecasts/${id}/actual`, data);

export const deleteForecast = (id: string) =>
  apiDelete(`/api/v1/forecasts/${id}`);
